import { useEffect, useState } from "react";

/**
 * NOW, FOR DISPLAY ONLY — and only while there is a server deadline to count
 * towards. It re-renders once a second until `until` passes and then stops, so
 * a screen with nothing pending does not tick at all.
 *
 * It decides nothing. Whether a resend is allowed is `canRequestCode` against
 * Backend's `resendAfter`; this only keeps the visible wait current.
 *
 * A BACKGROUNDED TAB IS RESYNCED ON RETURN, as `useHold`'s countdown is. A
 * locked phone stops firing intervals, so while a deadline is pending the
 * clock is also re-read the moment the page becomes visible again, rather than
 * showing a wait that has already passed for up to a second.
 */
export function useNow(until: number | null, clock: () => number = Date.now): number {
  const [now, setNow] = useState(clock);
  useEffect(() => {
    setNow(clock());
    if (until === null || clock() >= until) return;
    const deadline = until;
    const tick = () => {
      const t = clock();
      setNow(t);
      if (t >= deadline) stop();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    const id = window.setInterval(tick, 1000);
    const stop = () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    document.addEventListener("visibilitychange", onVisible);
    return stop;
  }, [until, clock]);
  return now;
}
